import React, { createContext, useEffect, useReducer, useState, ReactNode, useCallback } from 'react';

export interface StateType {
    [key: string]: any;
}
export interface Action {
    type: string;
    payload?: Partial<StateType>;
}

interface ContextStateProps {
    state: StateType;
    dispatch: React.Dispatch<Action>;
    refetchInitiate: () => Promise<void>;
}

const initialState: StateType = {};

export const ContextState = createContext<ContextStateProps>({
    state: initialState,
    dispatch: () => {},
    refetchInitiate: async () => {},
});

const reducer = (state: StateType, action: Action): StateType => {
    switch (action.type) {
        case 'setState':
            return { ...state, ...action.payload };
        default:
            return state;
    }
};

interface ContextStateProviderProps {
    children: ReactNode;
}

export const ContextStateProvider: React.FC<ContextStateProviderProps> = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const [loading, setLoading] = useState(true);
    const [isInitialMount, setIsInitialMount] = useState(true);
    const userId = state.user?.id;

    const fetchData = useCallback(async (isInitialFetch = false) => {
        if (isInitialFetch) {
            setLoading(true);
        }
        
        try {
            const response = await fetch('/api/initiate');
           
            if (!response.ok) {
                throw new Response(`HTTP error! status: ${response.status}`, { status: response.status });
            }
            const data = await response.json();
            dispatch({
                type: 'setState',
                payload: data,
            });
           
        } catch (error) {
            console.error('[ContextState] Error fetching state:', error);
        } finally {
            if (isInitialFetch) {
                setLoading(false);
                setIsInitialMount(false);
            }
        }
    }, []);

    // Initial data fetch and periodic refresh
    useEffect(() => {
        // Initial fetch with loading state
        fetchData(isInitialMount);
        
        // Set up periodic refresh every 10 minutes
        const refreshInterval = setInterval(() => {
            fetchData(false); // Don't change loading state on refresh
        }, 10 * 60 * 1000); // 10 minutes in milliseconds
        
        // Clean up interval on unmount
        return () => clearInterval(refreshInterval);
    }, [fetchData, userId, isInitialMount]);

    return (
        <ContextState.Provider
            value={{
                state,
                dispatch,
                refetchInitiate: () => fetchData(false),
            }}
        >
            {!loading && children}
        </ContextState.Provider>
    );
};